import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Archive, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError, StatusBadge } from "./api";
import { ImageUploader } from "./ImageUploader";
import { useAdmin } from "./AdminContext";

const TYPES = ["Detached", "Semi-Detached", "Terrace", "Corner Terrace", "Bungalow", "Good Class Bungalow", "Cluster House", "Other"];
const TENURES = ["Freehold", "999-year", "99-year", "Other"];
const DISTRICTS = ["D2", "D3", "D4", "D5", "D8", "D9", "D10", "D11", "D12", "D13", "D14", "D15", "D16", "D17", "D18", "D19", "D20", "D21", "D22", "D23", "D25", "D26", "D27", "D28"];

const EMPTY = {
  title: "", ref: "", type: "Terrace", tenure: "Freehold", district: "", location: "",
  price_value: "", listing_mode: "sale", description: "",
  land_area: "", built_up: "", beds: "", baths: "", storeys: "", parking: "",
  features: [], amenities: "", images: [], cover_index: 0, video_url: "", availability: "available",
};

const inputCls = "w-full border border-ivory/20 bg-white/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-ivory focus:outline-none";
const labelCls = "eyebrow mb-2 block text-ivory/60";

const Field = ({ label, error, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className={labelCls}>{label}</label>
    {children}
    {error && <p className="mt-2 border-l-2 border-brand pl-2 text-xs text-ivory" data-testid={`error-${htmlFor}`}>{error}</p>}
  </div>
);

const Section = ({ title, hint, children, testId }) => (
  <section className="border border-ivory/10 bg-[#242424] p-6 sm:p-8" data-testid={testId}>
    <h2 className="font-serif text-2xl text-ivory">{title}</h2>
    {hint && <p className="mt-1 text-xs text-ivory/50">{hint}</p>}
    <div className="mt-6">{children}</div>
  </section>
);

export default function AdminEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAdmin();
  const [form, setForm] = useState(EMPTY);
  const [published, setPublished] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [featureDraft, setFeatureDraft] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    adminApi.get(`/listings/${id}`)
      .then((r) => {
        const d = r.data;
        setForm({
          title: d.title || "", ref: d.ref || "", type: d.type || "Terrace", tenure: d.tenure || "Freehold",
          district: d.district || "", location: d.location || "", price_value: d.price_value || "",
          listing_mode: d.listing_mode || "sale", description: d.description || "",
          land_area: d.land_area || "", built_up: d.built_up || "", beds: d.beds ?? "", baths: d.baths ?? "",
          storeys: d.storeys || "", parking: d.parking || "", features: d.features || [],
          amenities: d.amenities || "", images: d.images || [], cover_index: d.cover_index || 0,
          video_url: d.video_url || "", availability: d.availability || "available",
        });
        setPublished(Boolean(d.published));
      })
      .catch((e) => { toast.error(apiError(e)); navigate("/admin/listings"); })
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const addFeature = () => {
    const v = featureDraft.trim();
    if (!v) return;
    setForm((f) => ({ ...f, features: [...f.features, v] }));
    setFeatureDraft("");
  };

  const validate = (publishing) => {
    const er = {};
    if (form.title.trim().length < 2) er.title = "Please enter the property title.";
    if (!form.district.trim()) er.district = "Please choose the district.";
    if (form.location.trim().length < 2) er.location = "Please enter the street or estate.";
    if (!Number(form.price_value) || Number(form.price_value) <= 0) er.price_value = "Please enter a valid asking price.";
    if (publishing && form.images.length === 0) er.images = "Please add at least one photo before publishing.";
    setErrors(er);
    if (Object.keys(er).length) {
      toast.error("Please fix the highlighted fields.");
      return false;
    }
    return true;
  };

  const save = async (publish) => {
    if (!validate(publish)) return;
    setBusy(true);
    const payload = {
      ...form,
      price_value: Number(form.price_value),
      land_area: Number(form.land_area) || 0,
      built_up: Number(form.built_up) || 0,
      beds: Number(form.beds) || 0,
      baths: Number(form.baths) || 0,
      storeys: Number(form.storeys) || 0,
      parking: Number(form.parking) || 0,
      ref: form.ref.trim() || null,
      video_url: form.video_url.trim() || null,
      published: publish,
    };
    try {
      if (isEdit) {
        await adminApi.put(`/listings/${id}`, payload);
        toast.success(publish ? "Saved — the listing is live on the website." : "Saved as a draft.");
      } else {
        await adminApi.post("/listings", payload);
        toast.success(publish ? "Property published to the website." : "Draft saved.");
      }
      navigate("/admin/listings");
    } catch (e) {
      toast.error(apiError(e, "Could not save. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    if (!window.confirm("Archive this property? It will disappear from the website. You can restore it later.")) return;
    try {
      await adminApi.post(`/listings/${id}/archive`);
      toast.success("Listing archived.");
      navigate("/admin/listings");
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async () => {
    if (!window.confirm("Permanently delete this property? This cannot be undone.")) return;
    try {
      await adminApi.delete(`/listings/${id}`);
      toast.success("Listing deleted.");
      navigate("/admin/listings");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-ivory/50" /></div>;
  }

  return (
    <div data-testid="admin-editor">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/listings" data-testid="editor-back" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/60 hover:text-ivory">
            <ArrowLeft className="h-4 w-4" /> Properties
          </Link>
          <StatusBadge status={published ? form.availability : "draft"} />
        </div>
        {isEdit && (
          <div className="flex gap-2">
            <button onClick={archive} data-testid="editor-archive" className="flex items-center gap-2 border border-ivory/25 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/70 hover:bg-white/10">
              <Archive className="h-3.5 w-3.5" /> Archive
            </button>
            {user?.role === "owner" && (
              <button onClick={remove} data-testid="editor-delete" className="flex items-center gap-2 border border-ivory/25 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/70 hover:bg-white/10">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )}
          </div>
        )}
      </div>

      <h1 className="mt-4 font-serif text-3xl text-ivory sm:text-4xl">{isEdit ? "Edit property" : "Add a new property"}</h1>

      <div className="mt-8 space-y-8">
        <Section title="Basic information" testId="section-basic">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field label="Property title" error={errors.title} htmlFor="f-title">
                <input id="f-title" data-testid="f-title" className={inputCls} placeholder="e.g. Family Semi-Detached near Kembangan MRT" value={form.title} onChange={set("title")} />
              </Field>
            </div>
            <Field label="Reference number" error={errors.ref} htmlFor="f-ref">
              <input id="f-ref" data-testid="f-ref" className={inputCls} placeholder="Leave empty to auto-assign" value={form.ref} onChange={set("ref")} />
            </Field>
            <Field label="Listing is for" htmlFor="f-mode">
              <select id="f-mode" data-testid="f-mode" className={inputCls} value={form.listing_mode} onChange={set("listing_mode")}>
                <option value="sale" className="bg-charcoal">Sale</option>
                <option value="rent" className="bg-charcoal">Rent</option>
              </select>
            </Field>
            <Field label="Property type" htmlFor="f-type">
              <select id="f-type" data-testid="f-type" className={inputCls} value={form.type} onChange={set("type")}>
                {TYPES.map((t) => <option key={t} value={t} className="bg-charcoal">{t}</option>)}
              </select>
            </Field>
            <Field label="Tenure" htmlFor="f-tenure">
              <select id="f-tenure" data-testid="f-tenure" className={inputCls} value={form.tenure} onChange={set("tenure")}>
                {TENURES.map((t) => <option key={t} value={t} className="bg-charcoal">{t}</option>)}
              </select>
            </Field>
            <Field label="District" error={errors.district} htmlFor="f-district">
              <input id="f-district" data-testid="f-district" className={inputCls} placeholder="e.g. D15" list="district-list" value={form.district} onChange={set("district")} />
              <datalist id="district-list">{DISTRICTS.map((d) => <option key={d} value={d} />)}</datalist>
            </Field>
            <Field label="Location / street or estate" error={errors.location} htmlFor="f-location">
              <input id="f-location" data-testid="f-location" className={inputCls} placeholder="e.g. Telok Kurau" value={form.location} onChange={set("location")} />
            </Field>
            <Field label={form.listing_mode === "rent" ? "Monthly rent (SGD)" : "Asking price (SGD)"} error={errors.price_value} htmlFor="f-price">
              <input id="f-price" data-testid="f-price" inputMode="numeric" className={inputCls} placeholder="e.g. 4280000" value={form.price_value} onChange={set("price_value")} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description" htmlFor="f-description">
                <textarea id="f-description" data-testid="f-description" rows={5} className={`${inputCls} resize-none`} placeholder="A short, honest description buyers will read…" value={form.description} onChange={set("description")} />
              </Field>
            </div>
          </div>
        </Section>

        <Section title="Property details" hint="Leave a field empty if it doesn't apply." testId="section-details">
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
            {[["land_area", "Land area (sqft)"], ["built_up", "Built-up area (sqft)"], ["beds", "Bedrooms"], ["baths", "Bathrooms"], ["storeys", "Storeys"], ["parking", "Parking lots"]].map(([key, label]) => (
              <Field key={key} label={label} htmlFor={`f-${key}`}>
                <input id={`f-${key}`} data-testid={`f-${key}`} inputMode="numeric" className={inputCls} placeholder="0" value={form[key]} onChange={set(key)} />
              </Field>
            ))}
          </div>
          <div className="mt-5">
            <Field label="Features" htmlFor="f-feature">
              <div className="flex gap-2">
                <input id="f-feature" data-testid="f-feature" className={inputCls} placeholder="e.g. Private pool — press Add" value={featureDraft} onChange={(e) => setFeatureDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }} />
                <button type="button" onClick={addFeature} data-testid="f-feature-add" className="shrink-0 border border-ivory/25 px-5 text-xs font-semibold uppercase tracking-[0.15em] text-ivory hover:bg-white/10">Add</button>
              </div>
            </Field>
            {form.features.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="feature-chips">
                {form.features.map((f, i) => (
                  <span key={i} className="flex items-center gap-2 border border-ivory/20 bg-white/5 px-3 py-1.5 text-xs text-ivory">
                    {f}
                    <button type="button" onClick={() => setForm((prev) => ({ ...prev, features: prev.features.filter((_, idx) => idx !== i) }))} aria-label={`Remove feature ${f}`} className="text-ivory/50 hover:text-ivory"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="mt-5">
            <Field label="Nearby amenities (optional)" htmlFor="f-amenities">
              <textarea id="f-amenities" data-testid="f-amenities" rows={2} className={`${inputCls} resize-none`} placeholder="e.g. 5 min walk to Kembangan MRT; near CHIJ Katong" value={form.amenities} onChange={set("amenities")} />
            </Field>
          </div>
        </Section>

        <Section title="Photos" hint="The photo marked Cover appears first on the website. Use the arrows to reorder." testId="section-media">
          <ImageUploader images={form.images} coverIndex={form.cover_index} onChange={(imgs, cover) => { setForm((f) => ({ ...f, images: imgs, cover_index: cover })); setErrors((er) => ({ ...er, images: undefined })); }} />
          {errors.images && <p className="mt-3 border-l-2 border-brand pl-3 text-sm text-ivory" data-testid="error-images">{errors.images}</p>}
          <div className="mt-5">
            <Field label="Video tour link (optional)" htmlFor="f-video">
              <input id="f-video" data-testid="f-video" className={inputCls} placeholder="https://youtube.com/…" value={form.video_url} onChange={set("video_url")} />
            </Field>
          </div>
        </Section>

        <Section title="Status" hint="This is what visitors see on the website." testId="section-status">
          <Field label="Availability" htmlFor="f-availability">
            <select id="f-availability" data-testid="f-availability" className={`${inputCls} max-w-xs`} value={form.availability} onChange={set("availability")}>
              <option value="available" className="bg-charcoal">Available</option>
              <option value="under_offer" className="bg-charcoal">Under Offer</option>
              <option value="sold" className="bg-charcoal">Sold</option>
            </select>
          </Field>
        </Section>

        <div className="sticky bottom-0 flex flex-wrap items-center gap-3 border-t border-ivory/10 bg-charcoal/95 py-4 backdrop-blur" data-testid="editor-actions">
          <button onClick={() => save(false)} disabled={busy} data-testid="save-draft"
            className="flex items-center gap-2 border border-ivory/30 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-ivory hover:bg-white/10 disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {published ? "Unpublish (save as draft)" : "Save draft"}
          </button>
          <button onClick={() => save(true)} disabled={busy} data-testid="save-publish"
            className="flex items-center gap-2 bg-brand px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-ivory hover:bg-brand-dark disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {published ? "Save changes" : "Publish to website"}
          </button>
          <Link to="/admin/listings" className="px-4 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/60 hover:text-ivory">Cancel</Link>
        </div>
      </div>
    </div>
  );
}
